/**
 * src/pages/RadarCa.jsx
 * Radar de Compra Ágil (E2): qué le encontró el radar a cada tenant, y por qué.
 *
 * POR QUÉ EXISTE, Y PARA QUIÉN (Manuel, 30-10-2026). El hilo de CA dijo en su
 * recado del 27-10 que a ellos les basta «el JSON y el Excel», así que esta
 * pantalla no es para operar el radar: es para que Manuel pueda contestar UNA
 * pregunta —**¿esto está encontrando algo que valga la pena?**— sin leer JSON.
 * Por eso entra la tabla de tenants con su lista debajo, y NO la tabla de noches
 * (que dice si la máquina corrió, no si sirve) ni los cinco botones.
 *
 * SOLO LEE. Ni un POST. Es el paso 1 de tres y el límite es deliberado.
 *
 * 🔑 LA ZONA GRIS NO ES LO QUE VE EL TENANT, y es la decisión de diseño que más
 * condiciona esta pantalla. `app/services/radar_ca/lista.py` guarda hasta
 * `tope_gris` candidatas por debajo del corte «para mirarlas, no para
 * enseñarlas». Mezclarlas con la lista haría que se juzgara el radar por filas
 * que el producto nunca muestra, así que van detrás de un interruptor, aparte y
 * rotuladas. La cifra de cabecera es `n_lista`, nunca la suma.
 */
import { useState } from 'react'

import { Header } from '../components/layout/Header'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { BarraPestanas } from '../components/ui/Pestanas'
import { Pista } from '../components/ui/Pista'
import { Table } from '../components/ui/Table'
import { descargarRadarCaRevision } from '../api/radarCa'
import { useRadarCaLista, useRadarCaTenant, useRadarCaTenants } from '../hooks/useRadarCa'

// Los tres peldaños, con las palabras del hilo de CA.
const PELDANO = {
  1: { label: 'Cotizó', color: 'blue',
       ayuda: 'Ha cotizado alguna Compra Ágil: hay títulos propios con que comparar.' },
  2: { label: 'Ganó', color: 'green',
       ayuda: 'Ha ganado alguna: su perfil es el más fiable de los tres.' },
  3: { label: 'Aprendiendo', color: 'gray',
       ayuda: 'Todavía sin histórico propio de CA, así que el perfil se construye con lo que hay.' },
}

// ⚠️ Un peldaño que esta pantalla no conozca NO puede reventar ni pintarse como
// uno de los tres. Que se vea el número crudo y que se diga que es desconocido:
// es la lección de la banda de churn, donde un cuarto estado habría dado un
// KeyError en producción.
function peldanoDe(valor) {
  return PELDANO[valor] || {
    label: `peldaño ${valor}`, color: 'yellow',
    ayuda: 'Valor que esta pantalla no conoce: se enseña crudo en vez de encajarlo a la fuerza.',
  }
}

const TRAMO = {
  muy_parecida: { label: 'Muy parecida', color: 'green' },
  parecida:     { label: 'Parecida',     color: 'blue' },
}

const CLP = new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 })
const dinero = v => (Number.isFinite(v) ? CLP.format(v) : null)

// Cuántas filas se PINTAN de entrada. No es un tope de datos: el resto está
// cargado y se despliega con el botón. Es la decisión de Manuel del 28-08 para
// el ranking —«el límite pasa a ser de pintado, no de dato»— y aquí hace falta
// porque el propio backend publica `peso_bytes_lista`: las listas pesan.
const VISIBLES = 25

function fechaCorta(iso) {
  if (!iso) return null
  const [dia, hora] = iso.split('T')
  return hora ? `${dia} ${hora.slice(0, 5)}` : dia
}


// ── El panel del tenant: tres preguntas sobre el mismo, en el mismo sitio ───
//
// POR QUÉ PESTAÑAS Y NO TRES PANTALLAS (paso 2, Manuel 30-10-2026). Después del paso
// 1 la pregunta dejó de ser «¿esto funciona?» —contestada— y pasó a ser «cuando una
// lista me parezca rara, ¿cómo averiguo por qué sin leer JSON?». Hay dos formas de
// que una lista sea rara: que recomiende algo absurdo —eso ya lo contesta la pestaña
// Lista, con el «se parece a X por estas palabras»— o que NO recomiende nada, y esa
// tiene dos causas distintas que hasta hoy no se podían separar desde el panel:
// **su perfil no tiene con qué comparar** (pestaña Perfil) o **el barrido no leyó
// todas las páginas** (pestaña Noches).
//
// 🔑 Las tres son la MISMA pregunta sobre el MISMO tenant, así que partirlas en tres
// pantallas obligaría a recordar a quién estabas mirando. Es el mismo motivo por el
// que la lista se abre bajo su fila y no al pie de la página.
const PESTANAS = [
  { clave: 'lista',  label: 'Lista' },
  { clave: 'perfil', label: 'Perfil' },
  { clave: 'noches', label: 'Noches' },
]

function PanelDelTenant({ clienteId }) {
  const [pestana, setPestana] = useState('lista')
  return (
    <div style={{ padding: 'var(--space-4)' }}>
      <BarraPestanas pestanas={PESTANAS} activa={pestana} onCambio={setPestana} />
      <div style={{ marginTop: 'var(--space-3)' }}>
        {pestana === 'lista' && <ListaDeLaNoche clienteId={clienteId} />}
        {pestana === 'perfil' && <PerfilDelTenant clienteId={clienteId} />}
        {pestana === 'noches' && <NochesDelTenant clienteId={clienteId} />}
      </div>
    </div>
  )
}


// Las dos pestañas de abajo comparten la MISMA consulta: el endpoint devuelve el
// perfil y las noches juntos, y React Query lo resuelve con una sola petición.
function _usarFicha(clienteId) {
  return useRadarCaTenant(clienteId, { titulos: true, noches: 30 })
}

function _hueco(texto) {
  return <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-3)' }}>{texto}</div>
}

// §5: un 404 del radar es un HECHO («no tiene perfil todavía»), no una avería, y el
// backend manda el texto. Se enseña el suyo y no uno inventado aquí.
function _siFalla(error) {
  if (!error) return null
  const detalle = error.response?.data?.detail
  return (
    <div style={{ fontSize: 'var(--text-sm)',
                  color: error.response?.status === 404 ? 'var(--color-text-3)' : 'var(--color-error)' }}>
      {detalle || 'No se pudo leer este tenant'}
    </div>
  )
}


// ── Perfil: CONTRA QUÉ compara el radar ────────────────────────────────────
function PerfilDelTenant({ clienteId }) {
  const { data, isLoading, error } = _usarFicha(clienteId)
  if (isLoading) return _hueco('Cargando el perfil…')
  if (error) return _siFalla(error)

  const titulos = Array.isArray(data?.titulos) ? data.titulos : []
  const c = data?.cobertura

  return (
    <div>
      {data?.motivo && (
        <div style={{ marginBottom: 'var(--space-3)', padding: '8px 12px',
                      borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
                      color: 'var(--color-warn)', background: 'var(--color-warn-soft)',
                      border: '1px solid var(--color-warn-line)' }}>
          {data.motivo}
        </div>
      )}

      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)',
                    color: 'var(--color-text-3)', marginBottom: 'var(--space-3)' }}>
        {data?.n_cotizaciones} cotizadas · {data?.n_ganadas} ganadas ·{' '}
        {data?.n_organismos} organismos ·{' '}
        {data?.calculado_en ? `calculado ${fechaCorta(data.calculado_en)}`
                            : 'nunca se ha calculado'}
      </div>

      {/* La cobertura: de lo que cotizó de verdad ese mes, cuánto estaba en sus
          listas. ⚠️ `pct` llega a null cuando no cotizó NADA ese mes, y eso NO es
          una cobertura del 0 %: es que no hubo con qué medirla (§3). */}
      {c && (
        <div style={{ marginBottom: 'var(--space-3)', padding: '10px 12px',
                      borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
                      border: '1px solid var(--color-border)' }}>
          <b>Cobertura de {c.mes}:</b>{' '}
          {c.pct === null || c.pct === undefined
            ? <>no se puede medir — <b>no cotizó ninguna CA ese mes</b>, así que no hay
                con qué comparar. No es una cobertura del 0 %.</>
            : <>el radar le había puesto en lista <b>{c.cazadas}</b> de las{' '}
                <b>{c.cotizadas}</b> que cotizó — <b>{c.pct} %</b>.</>}
          {Number.isFinite(c.filas_por_dia) && (
            <span style={{ color: 'var(--color-text-3)' }}>
              {' '}· {c.noches_con_lista} noches con lista, {c.filas_por_dia} filas por día.
            </span>
          )}
        </div>
      )}

      {/* Los títulos SON la regla de comparación: si están vacíos, la lista no puede
          salir llena y el porqué es éste, no el barrido. */}
      <div style={{ marginBottom: 'var(--space-2)' }}>
        <b>Contra qué compara</b>{' '}
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)',
                       color: 'var(--color-text-3)' }}>
          ({titulos.length} {titulos.length === 1 ? 'título' : 'títulos'} suyos)
        </span>
      </div>
      {titulos.length === 0
        ? _hueco('Ni un título en su perfil, así que el radar no tiene con qué comparar: '
                 + 'una lista vacía de este tenant se explica aquí y no en el barrido.')
        : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {titulos.map((t, i) => (
              <span key={i} style={{
                padding: '4px 10px', borderRadius: 'var(--radius-md)',
                fontSize: 'var(--text-sm)', background: 'var(--color-surface-2)',
                border: '1px solid var(--color-border)',
              }}>{typeof t === 'string' ? t : (t?.t ?? JSON.stringify(t))}</span>
            ))}
          </div>
        )}
    </div>
  )
}


// ── Noches: SI EL BARRIDO FUE SANO ─────────────────────────────────────────
function NochesDelTenant({ clienteId }) {
  const { data, isLoading, error } = _usarFicha(clienteId)
  if (isLoading) return _hueco('Cargando sus noches…')
  if (error) return _siFalla(error)

  const noches = Array.isArray(data?.noches) ? data.noches : []
  if (noches.length === 0) {
    return _hueco('Este tenant no tiene ninguna noche de listas todavía.')
  }

  return (
    <div>
      {noches.map(n => {
        // 🔑 Lo que hace útil esta pestaña es distinguir las dos formas de noche
        // mala: la que NO leyó todo (`paginas_perdidas`) y la que leyó y no
        // encontró. La primera invalida el «0 en lista» de esa noche; la segunda
        // es una medida. Pintarlas igual sería el §3.
        const coja = Number.isFinite(n.paginas_perdidas) && n.paginas_perdidas > 0
        return (
          <div key={n.dia} style={{
            display: 'flex', gap: 'var(--space-3)', alignItems: 'baseline',
            flexWrap: 'wrap', padding: '6px 0',
            borderBottom: '1px solid var(--color-border)',
          }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)',
                           minWidth: '96px' }}>{n.dia}</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)' }}>
              <b>{n.n_lista}</b> en lista
              <span style={{ color: 'var(--color-text-3)' }}>
                {' '}· {n.n_gris} grises · {n.n_pool} miradas
                {Number.isFinite(n.segundos) ? ` · ${n.segundos} s` : ''}
              </span>
            </span>
            {coja && (
              <Badge color="yellow">
                {n.paginas_perdidas} {n.paginas_perdidas === 1 ? 'página perdida' : 'páginas perdidas'}
              </Badge>
            )}
            {n.motivo && (
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-warn)' }}>
                {n.motivo}
              </span>
            )}
          </div>
        )
      })}
      <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-xs)',
                    color: 'var(--color-text-3)' }}>
        Una noche con páginas perdidas <b>no midió del todo</b>: su «0 en lista» no
        significa que no hubiera nada, sino que no se leyó entero.
      </div>
    </div>
  )
}


// ── La lista de una noche, bajo la fila de su tenant ────────────────────────
function ListaDeLaNoche({ clienteId }) {
  const [gris, setGris] = useState(false)
  const [todas, setTodas] = useState(false)
  const { data, isLoading, error } = useRadarCaLista(clienteId, { gris })

  if (isLoading) return <div style={{ padding: 'var(--space-4)', color: 'var(--color-text-3)' }}>Cargando la lista…</div>

  // §5: un 404 aquí es un HECHO del radar («no tiene ninguna noche»), no una
  // avería del panel, y se lee distinto. Se enseña el texto que manda el backend.
  if (error) {
    const detalle = error.response?.data?.detail
    return (
      <div style={{ padding: 'var(--space-4)', fontSize: 'var(--text-sm)',
                    color: error.response?.status === 404 ? 'var(--color-text-3)' : 'var(--color-error)' }}>
        {detalle || 'No se pudo leer la lista de este tenant'}
      </div>
    )
  }

  const filas = Array.isArray(data?.filas) ? data.filas : []
  const deLista = filas.filter(f => f.zona === 'lista')
  const deGris = filas.filter(f => f.zona === 'gris')
  const pintadas = todas ? deLista : deLista.slice(0, VISIBLES)

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)',
                    flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
        <b>Lista del {data?.dia}</b>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)',
                       color: 'var(--color-text-3)' }}>
          {deLista.length} en lista · {data?.n_pool} candidatas miradas ·{' '}
          {Math.round((data?.peso_bytes_lista ?? 0) / 1024 * 10) / 10} KB
        </span>
      </div>

      {/* 🔴 DOS FUENTES PARA EL MISMO NUMERO, y por eso se comparan en voz alta.
          La fila del tenant pinta `ultima_noche.n_lista`, que es el contador que
          la noche GUARDO; este panel cuenta las filas que de verdad llegaron.
          Deberian coincidir — y si no coinciden, lo que hay es un desajuste entre
          el contador y las filas, que es justo lo que no se puede dejar mudo
          (§4: una regla, un sitio; §5: ningun fallo callado). Enseñar los dos
          numeros en dos cajas distintas sin decir nada seria peor que no
          enseñarlos: pareceria que miden cosas distintas. */}
      {Number.isFinite(data?.n_lista) && data.n_lista !== deLista.length && (
        <div style={{ marginBottom: 'var(--space-3)', padding: '8px 12px',
                      borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
                      color: 'var(--color-error)', background: 'var(--color-error-soft)',
                      border: '1px solid var(--color-error-line)' }}>
          La noche apuntó <b>{data.n_lista}</b> en lista y han llegado{' '}
          <b>{deLista.length}</b> filas. <b>No cuadran</b>, y el contador y las filas
          salen de la misma noche: uno de los dos está mal. Lo de abajo son las filas
          reales.
        </div>
      )}

      {/* El motivo viene del backend y se enseña TAL CUAL: «perfil sin títulos
          (peldaño 3)», «lectura parcial: N páginas». Un «no hay CAs» inventado
          sobre una lectura parcial sería un cero que nadie midió (§3). */}
      {data?.motivo && (
        <div style={{ marginBottom: 'var(--space-3)', padding: '8px 12px',
                      borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
                      color: 'var(--color-warn)', background: 'var(--color-warn-soft)',
                      border: '1px solid var(--color-warn-line)' }}>
          {data.motivo}
        </div>
      )}

      {deLista.length === 0 && !data?.motivo && (
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-3)' }}>
          Esa noche el radar no le encontró ninguna Compra Ágil para la lista, y lo dice
          habiendo mirado {data?.n_pool} candidatas.
        </div>
      )}

      {pintadas.map(f => <FilaCa key={`${f.zona}-${f.codigo}`} f={f} />)}

      {!todas && deLista.length > VISIBLES && (
        <Button size="sm" variant="ghost" onClick={() => setTodas(true)}>
          Ver las {deLista.length - VISIBLES} restantes
        </Button>
      )}

      {/* La zona gris, detrás de un interruptor y dicha por su nombre. */}
      <div style={{ marginTop: 'var(--space-4)', paddingTop: 'var(--space-3)',
                    borderTop: '1px solid var(--color-border)' }}>
        <Button size="sm" variant="ghost" onClick={() => { setGris(!gris) }}>
          {gris ? 'Ocultar' : 'Ver'} la zona gris{data?.n_gris ? ` (${data.n_gris})` : ''}
        </Button>
        <span style={{ marginLeft: 'var(--space-2)', fontSize: 'var(--text-xs)',
                       color: 'var(--color-text-3)' }}>
          Las que quedaron por debajo del corte. <b>El tenant NO las ve</b>: se guardan
          para mirarlas, no para enseñarlas.
        </span>
        {gris && deGris.map(f => <FilaCa key={`gris-${f.codigo}`} f={f} gris />)}
      </div>
    </div>
  )
}


function FilaCa({ f, gris = false }) {
  // 🔑 `titulo`, `organismo`, `region` y las fechas vienen de un OUTER JOIN con
  // `ca_procesos`: si no hay ficha del proceso, esas claves NO LLEGAN —no llegan
  // vacías, no llegan—. Pintar `titulo || '—'` diría «esta compra no tiene
  // título», que es falso: lo que falta es su ficha. Se distingue con `in`, no
  // con `??`, igual que `cortado` en el bloque del censo.
  const sinFicha = !('titulo' in f)
  const tramo = TRAMO[f.tramo]
  return (
    <div data-zona={f.zona} data-sin-ficha={sinFicha ? 'si' : 'no'} style={{
      padding: '10px 12px', marginBottom: '6px', borderRadius: 'var(--radius-md)',
      border: '1px solid var(--color-border)',
      background: gris ? 'var(--color-surface-2)' : 'transparent',
      opacity: gris ? 0.75 : 1,
    }}>
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)',
                       color: 'var(--color-text-3)' }}>
          {f.zona === 'lista' ? `#${f.posicion}` : 'gris'} · {f.codigo}
        </span>
        {tramo && <Badge color={tramo.color}>{tramo.label}</Badge>}
        {Number.isFinite(f.te_compra_ocs) && f.te_compra_ocs > 0 && (
          <Badge color="purple">te compra · {f.te_compra_ocs} OCs</Badge>
        )}
      </div>

      <div style={{ marginTop: 'var(--space-1)' }}>
        {sinFicha
          ? <>
              <i style={{ color: 'var(--color-text-3)' }}>
                Sin ficha del proceso guardada, así que no hay título ni organismo que
                enseñar. No es que esta compra no los tenga.
              </i>
              {/* ⚠️ El monto SÍ sale, y la primera versión lo escondía. `monto_clp`
                  viene de la fila de la lista, no de `ca_procesos`, así que está
                  siempre: callarlo porque falta la ficha es dejar de enseñar un dato
                  que sí tenemos. Lo destapó el guardián al escribirlo, no la lectura
                  del código. */}
              {dinero(f.monto_clp) && (
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-3)' }}>
                  {dinero(f.monto_clp)}
                </div>
              )}
            </>
          : <>
              <div>{f.titulo}</div>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-3)' }}>
                {[f.organismo, f.region, dinero(f.monto_clp),
                  f.fecha_cierre ? `cierra ${fechaCorta(f.fecha_cierre)}` : null]
                  .filter(Boolean).join(' · ')}
              </div>
            </>}
      </div>

      {/* El PORQUÉ, que es lo que hace auditable la recomendación: a qué título
          suyo se parece y con qué palabras. Sin esto la lista es un oráculo. */}
      {f.parecida_a && (
        <div style={{ marginTop: 'var(--space-1)', fontSize: 'var(--text-sm)',
                      color: 'var(--color-text-3)' }}>
          Se parece a lo suyo: <b>{f.parecida_a}</b>
          {f.palabras_comunes?.length ? <> — por «{f.palabras_comunes.join('», «')}»</> : null}
          {Number.isFinite(f.similitud) ? <> · {Math.round(f.similitud * 100)} %</> : null}
        </div>
      )}
    </div>
  )
}


// ── El Excel de revisión a ciegas ──────────────────────────────────────────
//
// 🔑 Es el único instrumento de todo el radar que contesta «¿ACIERTA?» en vez de
// «¿funcionó?»: una pestaña por tenant con un desplegable S/N/?, diseñado por el hilo
// de CA para juzgar la calidad sin saber de antemano la respuesta. Debería haber
// entrado en el paso 1 y no lo puse.
//
// ⚠️ El fallo se DICE. Una descarga que no pasa nada no se distingue de una que no
// se pulsó, y ese silencio es el §5 — más caro aquí que en otros sitios porque el
// navegador tampoco avisa: no hay pestaña nueva ni error visible.
function BotonRevision() {
  const [bajando, setBajando] = useState(false)
  const [fallo, setFallo] = useState(null)

  async function bajar() {
    setBajando(true)
    setFallo(null)
    try {
      await descargarRadarCaRevision()
    } catch (e) {
      setFallo(e.response?.data?.detail || e.message || 'No se pudo descargar')
    } finally {
      setBajando(false)
    }
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
      {fallo && (
        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-error)',
                       fontFamily: 'var(--font-mono)' }}>✗ {fallo}</span>
      )}
      <Button size="sm" variant="ghost" onClick={bajar} loading={bajando}>
        Revisión a ciegas (.xlsx)
      </Button>
    </span>
  )
}


// ── La página ───────────────────────────────────────────────────────────────
export default function RadarCa() {
  const [abierta, setAbierta] = useState(null)
  const { data, isLoading, error } = useRadarCaTenants()
  const tenants = Array.isArray(data?.tenants) ? data.tenants : []

  const columnas = [
    {
      key: 'cliente_id', label: 'Tenant', width: '190px',
      render: (v, row) => (
        <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
          <span>{v}</span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)',
                         color: 'var(--color-text-3)' }}>
            {row.ruts?.length ? row.ruts.join(' · ') : 'sin RUT en el perfil'}
          </span>
        </span>
      ),
    },
    {
      key: 'peldano', label: 'Peldaño', width: '140px',
      render: v => {
        const p = peldanoDe(v)
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <Badge color={p.color}>{p.label}</Badge>
            <Pista etiqueta={p.label} contenido={p.ayuda} />
          </span>
        )
      },
    },
    {
      key: 'n_cotizaciones', label: 'CA cotizadas', width: '120px', align: 'right',
      render: (v, row) => (
        <span style={{ fontFamily: 'var(--font-mono)' }}>
          {v}{row.n_ganadas ? <span style={{ color: 'var(--color-ok)' }}> · {row.n_ganadas} ganadas</span> : null}
        </span>
      ),
    },
    {
      key: 'n_titulos', label: 'Títulos del perfil', width: '140px', align: 'right',
      render: v => <span style={{ fontFamily: 'var(--font-mono)' }}>{v}</span>,
    },
    {
      key: 'ultima_noche', label: 'Última noche', width: '230px',
      // §3: «nunca ha corrido» y «corrió y no encontró nada» son dos cosas, y
      // pintar un 0 en la primera sería un cero que nadie midió.
      render: n => n ? (
        <span data-ultima-noche="si" style={{ display: 'inline-flex', flexDirection: 'column' }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)' }}>
            {n.dia} · <b>{n.n_lista}</b> en lista
          </span>
          {n.motivo && (
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-warn)' }}>{n.motivo}</span>
          )}
        </span>
      ) : (
        <span data-ultima-noche="nunca"
              style={{ color: 'var(--color-text-3)', fontSize: 'var(--text-sm)' }}>
          nunca se le ha generado lista
        </span>
      ),
    },
  ]

  return (
    <div>
      <Header title="Radar de Compra Ágil"
              subtitle="Qué le encontró el radar a cada tenant, y por qué. Solo lectura." />

      <Card title="Tenants con perfil del radar"
            subtitle="Pincha una fila para ver su lista, su perfil y sus noches."
            action={<BotonRevision />}>
        {error
          ? <div style={{ color: 'var(--color-error)', fontFamily: 'var(--font-mono)',
                          fontSize: 'var(--text-sm)' }}>
              {error.response?.data?.detail || 'No se pudo leer el radar'}
            </div>
          : <Table
              columns={columnas}
              data={tenants}
              loading={isLoading}
              emptyMessage="Ningún tenant tiene perfil del radar todavía. El perfil se calcula cada noche."
              claveFila={row => row.cliente_id}
              onRowClick={row => setAbierta(abierta === row.cliente_id ? null : row.cliente_id)}
              claveExpandida={abierta}
              renderExpandida={row => <PanelDelTenant clienteId={row.cliente_id} />}
            />}
      </Card>
    </div>
  )
}
